import { forwardRef, useImperativeHandle, useState } from 'react'
import { useRouter } from 'next/router'

import { Button } from '@wartek-id/button-v1'
import { Text } from '@wartek-id/text-v1'
import { useToast } from '@wartek-id/toast'

import PanelKinerja from 'app/KinerjaPage/components/PanelKinerja'
import {
  TableBody,
  TableHeader,
  TableHeaderItem,
  TablePelaksanaanKinerja,
  TableRow,
  TableRowItem,
} from 'app/KinerjaPage/components/TablePelaksanaanKinerja'
import DialogFinalizeSKP from './components/DialogFinalizeSKP'
import useContentContext from 'app/KinerjaPage/hooks/useContentContext'
import {
  useAPIKinerjaProfile,
  useAPIUpdateState,
} from 'api/kinerja/useAPIKinerja'
import StickyFooter from 'app/KinerjaPage/components/StickyFooter'
import useSKPTrackEvent from 'app/KinerjaPage/hooks/useSKPTrackEvent'
import useToggleSdkContext from 'utils/useToogleSdk'
import { tugasPokok, tugasPokokPamong } from 'app/KinerjaPage/constants'
import PanelKinerjaCompact from 'app/KinerjaPage/components/PanelKinerjaCompact'
import { usePengelolaanKinerjaBaseUrl } from 'app/KinerjaShared/hooks/usePengelolaanKinerjaBaseUrl'
import { assetUrl } from 'utils/url'

import type { ForwardedRef } from 'react'
import type {
  KinerjaPerencanaanTabBaseProps,
  KinerjaRoleType,
  PerilakuKerjaPayload,
  SKPTabHandler,
} from 'types/kinerja'

interface PerencanaanTabRangkumanProps extends KinerjaPerencanaanTabBaseProps {
  onRequestChange?: () => void
  onClickBackToHome?: () => void
  role?: KinerjaRoleType
}

const TabRangkuman = (
  {
    skp,
    isSubmitting,
    onRequestChange = () => {},
    onClickBackToHome = () => {},
    role = 'GURU',
  }: PerencanaanTabRangkumanProps,
  ref: ForwardedRef<SKPTabHandler>
) => {
  const {
    getKinerjaUtamaById,
    getPengembanganKompetensiById,
    getTugasTambahanById,
    getPraktikPembelajaranById,
    getAspekPerilakuKerjaById,
  } = useContentContext()
  const router = useRouter()
  const toast = useToast()
  const { trackEvent } = useSKPTrackEvent()
  const toggles = useToggleSdkContext()
  const baseUrl = usePengelolaanKinerjaBaseUrl()

  const isHidePointEnabled =
    toggles?.['kinerja-hide-rhk-point-enabled'] === true
  const isTugasPokokEnabled =
    toggles?.['kinerja-tugas-pokok-enabled'] === true && skp?.tahun > 2024
  const isNewFlow = isHidePointEnabled && skp.tahun > 2024

  const isSPNFEnabled = toggles?.['kinerja-spnf-enabled'] === true

  const isPamong = role === 'GURU_SPNF'
  const selectedTugasPokok = isPamong ? tugasPokokPamong : tugasPokok
  const tugasTambahanDropdownProps = (i: number) => {
    return isPamong
      ? {
          number: `${selectedTugasPokok.length + i + 1}`,
          label:
            'Terlaksananya penugasan tambahan dalam mendukung pembelajaran yang bermutu untuk semua pada satuan pendidikan nonformal',
          numberingType: 'default' as const,
        }
      : {
          number: `${selectedTugasPokok.length + 1}.${i + 1}`,
          numberingType: 'group' as const,
        }
  }

  const getPerilakuKerjaRow = (value: PerilakuKerjaPayload) => {
    const aspek = getAspekPerilakuKerjaById(value.aspekId)
    const indikator = aspek?.indikator.find(
      ({ id }) => id === value.indikatorId
    )

    const ekspektasi = indikator?.ekspektasi.find(
      ({ id }) => id === value.ekspektasiId
    )

    return {
      aspek: aspek?.value ?? '',
      indikator: indikator?.value ?? '',
      ekspektasi: ekspektasi?.value ?? '',
    }
  }

  const selectedPraktikPembelajaran = getPraktikPembelajaranById(
    skp.praktikPembelajaran
  )

  const filteredTugasTambahan = skp.tugasTambahan.filter(
    (value) => value.id !== 0
  )

  const [isOpenDialogFinalize, setIsOpenDialogFinalize] = useState(false)

  const { data: profile } = useAPIKinerjaProfile()
  const { mutateAsync: finalizeSKP, isLoading: isFinalizingSKP } =
    useAPIUpdateState()

  const handleClickFinalize = async () => {
    trackEvent({
      event_name: 'CLICK_KINERJA_PROPOSE_PLANNING',
      screen_name: 'KINERJA_PLANNING_PENGAJUAN_POP_UP',
      kinerja_planning_id: skp?.id,
      kinerja_planning_status: skp?.status,
    })

    try {
      await finalizeSKP({
        skpId: skp.id,
        ...(isSPNFEnabled ? { role } : {}),
      })

      onClickBackToHome()
    } catch (e) {
      console.error(e)

      toast({
        message: 'Gagal mengajukan SKP',
      })
    }
  }

  useImperativeHandle(
    ref,
    () => ({
      saveDraft() {
        router.push(baseUrl)
      },
    }),
    [router, baseUrl]
  )

  return (
    <div>
      <div className="py-9 px-6">
        <div className="flex flex-col gap-1">
          <Text variant="heading-md">Rangkuman Perencanaan Kinerja Anda</Text>
          <Text color="subdued" as="ul" className="list-disc pl-5">
            <li>
              Anda dapat cek ulang dan ubah sebelum mengirim ke Kepala Sekolah.
            </li>
            <li>
              Format kalimat Perencanaan Kinerja sudah kami sesuaikan dengan
              aturan yang berlaku. Mohon dicek, ya.
            </li>
          </Text>
        </div>

        {isTugasPokokEnabled ? (
          <section className="mt-9">
            <div className="flex flex-col gap-4">
              {/* Section Tugas Pokok */}
              <div className="flex gap-3 items-center">
                <img
                  src={assetUrl('/illustrations/img-badge-star-ribbon-pro.png')}
                  className="flex-shrink-0 flex-grow-0"
                  alt=""
                  width={52}
                  height={52}
                />
                <Text variant="heading-sm">Pelaksanaan Tugas Pokok</Text>
              </div>
              {selectedTugasPokok.map((tugas, i) => (
                <PanelKinerjaCompact
                  key={`tugasPokok-${i}`}
                  number={i + 1}
                  rhk={tugas}
                />
              ))}

              {filteredTugasTambahan.map((kinerja, i) => (
                <PanelKinerjaCompact
                  key={`tugasTambahan-${i}`}
                  rhk={getTugasTambahanById(kinerja.id).rencanaHasilKerja}
                  {...tugasTambahanDropdownProps(i)}
                />
              ))}

              {/* Section Praktik Kinerja */}
              <div className="flex gap-3 items-center">
                <img
                  src={assetUrl('/illustrations/img-badge-star.png')}
                  className="flex-shrink-0 flex-grow-0"
                  alt=""
                  width={52}
                  height={52}
                />
                <Text variant="heading-sm">Praktik Kinerja</Text>
              </div>
              <PanelKinerjaCompact
                number={1}
                rhk={selectedPraktikPembelajaran.rencanaHasilKerja}
              />

              {/* Section Pengembangan Kompetensi */}
              <div className="flex gap-3 items-center">
                <img
                  src={assetUrl('/illustrations/img-document.png')}
                  className="flex-shrink-0 flex-grow-0"
                  alt=""
                  width={52}
                  height={52}
                />
                <Text variant="heading-sm">Pengembangan Kompetensi</Text>
              </div>
              {skp.pengembanganKompetensi.map((kinerja, i) => {
                const selectedPengembanganKompetensi =
                  getPengembanganKompetensiById(kinerja.id)

                return (
                  <PanelKinerjaCompact
                    key={`PengembanganKompetensi-${i}`}
                    number={i + 1}
                    rhk={selectedPengembanganKompetensi.rencanaHasilKerja}
                  />
                )
              })}
            </div>
          </section>
        ) : (
          <>
            <section className="mt-9">
              <Text as="h2" variant="heading-sm" className="mb-4">
                Hasil Kerja Utama
              </Text>

              <div className="flex flex-col gap-6">
                <PanelKinerja
                  key={`PanelKinerjaUtama-PraktikPembelajaran`}
                  data-testid={`PanelKinerjaUtama-PraktikPembelajaran`}
                  number="1"
                  kinerja={selectedPraktikPembelajaran}
                  additionalInfo="Guru akan mengisi Formulir Diskusi Persiapan, Formulir Diskusi Tindak Lanjut, dan Formulir Refleksi Tindak Lanjut."
                  showDetails={!isNewFlow}
                />

                {skp.pengembanganKompetensi.map((kinerja, idx) => {
                  const selectedPengembanganKompetensi =
                    getPengembanganKompetensiById(kinerja.id)

                  return (
                    <PanelKinerja
                      key={`PanelKinerja-PengembanganKompetensi-${idx}`}
                      data-testid={`PanelKinerja-PengembanganKompetensi-${idx}`}
                      showDetails={!isNewFlow}
                      number={idx + 2}
                      label={
                        isNewFlow
                          ? 'Indikator Kompetensi'
                          : 'Rencana Hasil Kerja'
                      }
                      kinerja={{
                        ...selectedPengembanganKompetensi,
                        targetKuantitas: `${kinerja.kuantitas} kegiatan`,
                        bobot:
                          selectedPengembanganKompetensi.bobot *
                          kinerja.kuantitas,
                      }}
                    />
                  )
                })}

                {skp.kinerjaUtama.map((kinerja, idx) => {
                  const kinerjaUtama = getKinerjaUtamaById(kinerja)

                  return (
                    <PanelKinerja
                      key={`PanelKinerjaUtama-${kinerja}`}
                      data-testid={`PanelKinerjaUtama-${kinerja}`}
                      kinerja={kinerjaUtama}
                      number={idx + skp.pengembanganKompetensi.length + 2}
                      showDetails={!isNewFlow}
                    />
                  )
                })}
              </div>
            </section>

            {filteredTugasTambahan.length > 0 && (
              <section className="mt-9">
                <Text as="h2" variant="heading-sm" className="mb-4">
                  Hasil Kerja Tambahan
                </Text>

                <div className="flex flex-col gap-6">
                  {filteredTugasTambahan.map((kinerja, idx) => {
                    const selectedTugasTambahan = getTugasTambahanById(
                      kinerja.id
                    )

                    return (
                      <PanelKinerja
                        key={`PanelKinerjaTambahan-${idx}`}
                        data-testid={`PanelKinerjaTambahan-${idx}`}
                        number={idx + 1}
                        kinerja={selectedTugasTambahan}
                        showDetails={!isNewFlow}
                      />
                    )
                  })}
                </div>
              </section>
            )}
          </>
        )}

        <section className="mt-9">
          <Text as="h2" variant="heading-sm">
            Perilaku Kerja
          </Text>

          <TablePelaksanaanKinerja>
            <TableHeader>
              <TableHeaderItem>No.</TableHeaderItem>
              <TableHeaderItem>Aspek</TableHeaderItem>
              <TableHeaderItem>Indikator Perilaku</TableHeaderItem>
              <TableHeaderItem>Ekspektasi Khusus Pimpinan</TableHeaderItem>
            </TableHeader>
            <TableBody>
              {skp.prilakuKerja
                .map(getPerilakuKerjaRow)
                .map((perilakuKerja, idx) => (
                  <TableRow key={`perilaku-kerja-${idx}`}>
                    <TableRowItem>{idx + 1}</TableRowItem>
                    <TableRowItem>{perilakuKerja.aspek}</TableRowItem>
                    <TableRowItem>{perilakuKerja.indikator}</TableRowItem>
                    <TableRowItem>{perilakuKerja.ekspektasi}</TableRowItem>
                  </TableRow>
                ))}
            </TableBody>
          </TablePelaksanaanKinerja>
        </section>
      </div>

      <StickyFooter>
        <Button size="lg" color="white" onClick={onRequestChange}>
          Ubah
        </Button>
        <Button
          size="lg"
          isLoading={isSubmitting}
          disabled={isSubmitting}
          type="submit"
          data-testid="btn-next"
          onClick={() => {
            trackEvent({
              event_name: 'CLICK_KINERJA_PLANNING_TO_POP_UP_CONFIRMATION',
              screen_name: 'KINERJA_PLANNING_PERILAKU_KERJA_PAGE',
              kinerja_planning_id: skp?.id,
              kinerja_planning_status: skp?.status,
            })
            setIsOpenDialogFinalize(true)
          }}
        >
          Ajukan Perencanaan
        </Button>

        <DialogFinalizeSKP
          data-testid="dialog-finalize-skp"
          isOpen={isOpenDialogFinalize}
          onClose={() => setIsOpenDialogFinalize(false)}
          isFinalizing={isFinalizingSKP}
          onClickCancel={() => {
            setIsOpenDialogFinalize(false)
          }}
          onClickFinalize={handleClickFinalize}
          satuanPendidikan={profile?.data.satuanPendidikan}
        />
      </StickyFooter>
    </div>
  )
}

export default forwardRef<SKPTabHandler, PerencanaanTabRangkumanProps>(
  TabRangkuman
)
